import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OidcSecurityService, UserDataResult } from 'angular-auth-oidc-client';
import { catchError, firstValueFrom, map, of, switchMap } from 'rxjs';

import { RETURN_URL_KEY } from '@core/guards/auth-guard';

function tokenRoles(payload: { roles?: unknown } | null): string[] {
  return Array.isArray(payload?.roles) ? payload.roles.filter((role): role is string => typeof role === 'string') : [];
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  readonly isAuthenticated = toSignal(
    this.oidcSecurityService.isAuthenticated$.pipe(map((authenticationResult) => authenticationResult.isAuthenticated)),
    {
      initialValue: false
    }
  );

  readonly userData = toSignal(this.oidcSecurityService.userData$);

  /**
   * App roles the access token carries, read again on every sign-in, token refresh and sign-out. Empty while signed
   * out, and when the token cannot be decoded.
   */
  readonly roles = toSignal(
    this.oidcSecurityService.isAuthenticated$.pipe(
      switchMap(({ isAuthenticated }) =>
        isAuthenticated
          ? this.oidcSecurityService.getPayloadFromAccessToken().pipe(
              map(tokenRoles),
              catchError(() => of<string[]>([]))
            )
          : of<string[]>([])
      )
    ),
    { initialValue: [] as string[] }
  );

  /**
   * Settles the sign-in state before a guard reads it. A guard that runs ahead of the auth guard, as a `canMatch`
   * does on a page load, finds nothing checked yet, so this runs the same check.
   */
  async awaitReady(): Promise<void> {
    if (!this.isAuthenticated()) {
      await firstValueFrom(this.oidcSecurityService.checkAuth());
    }
  }

  /** Starts sign-in. The auth guard sends the user to `returnUrl` once they are back. */
  login(returnUrl?: string): void {
    if (returnUrl) {
      sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
    }
    this.oidcSecurityService.authorize();
  }

  async logout(): Promise<void> {
    await firstValueFrom(this.oidcSecurityService.logoffAndRevokeTokens());
  }

  async getUserProfile(): Promise<UserDataResult | undefined> {
    return await firstValueFrom(this.oidcSecurityService.getPayloadFromIdToken());
  }
}
